/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// models
import GoogleMap from "models/google-map/GoogleMap.js";
import GoogleStreetView from "models/google-street-view/GoogleStreetView.js";

// orchestration
import Firebase from "orchestration/firebase-library/Firebase.js";

// utils
import VirtualDomUtils from "utils/virtual-dom-utils/VirtualDomUtils.js";

// constants - local
const GOOGLE_MAPS_API_PREFIX = "https://maps.googleapis.com/maps/api/js?key=";
const GOOGLE_MAPS_ROUTE_PREFIX = "https://www.google.com/maps/dir/?api=1&destination=";
const GOOGLE_MAPS_ROUTE_SUFFIX = "&travelmode=walking";
const MAP_LAT_DEFAULT = Number("38.449151");
const MAP_LNG_DEFAULT = Number("-99.149291"); // center of U.S.
const MAP_ZOOM_DEFAULT = Number("3.0");

class GoogleMapsService {

  /*====================
  API Management
  ====================*/
  static getApiKey() {
    return Firebase.getGoogleMapsApiKey();
  }

  static initialize() {
    return new Promise((resolve, reject) => {
      GoogleMapsService.getApiKey()
      .then((apiKey) => {
        const mapsUrl = `${GOOGLE_MAPS_API_PREFIX}${apiKey}`;
        return VirtualDomUtils.mountScript(mapsUrl);
      })
      .then(() => resolve())
      .catch((error) => reject(error));
    });
  }

  /*====================
  Application specific values
  ====================*/
  static APPLICATION_CENTER = {
    lat: Number(MAP_LAT_DEFAULT),
    lng: Number(MAP_LNG_DEFAULT)
  };
  static APPLICATION_ZOOM = Number(MAP_ZOOM_DEFAULT);

  static getApplicationMapProperties() {
    return {
      center: {
        lat: Number(GoogleMapsService.APPLICATION_CENTER.lat),
        lng: Number(GoogleMapsService.APPLICATION_CENTER.lng)
      },
      zoom: Number(GoogleMapsService.APPLICATION_ZOOM)
    };
  }

  static getApplicationStreetViewProperties() {
    return {
      position: {
        lat: Number(GoogleMapsService.APPLICATION_CENTER.lat),
        lng: Number(GoogleMapsService.APPLICATION_CENTER.lng)
      },
      pov: {
        heading: Number("165.0"),
        pitch: Number("0.0"),
        zoom: Number("1.0")
      }
    };
  }

  static getApplicationUiProperties() {
    return {};
  }

  /*====================
  Map Management
  ====================*/
  static applyCustomization(application) {
    const applicationCenter = application.getMapPosition();
    const applicationZoom = application.getMapZoom();
    GoogleMapsService.APPLICATION_CENTER = { ...applicationCenter };
    GoogleMapsService.APPLICATION_ZOOM = Number(applicationZoom);
  }

  static async createMap(elementId, mapProperties, streetViewProperties, uiProperties, plugins) {
    await GoogleMapsService.initialize();

    // create GoogleMap model
    const mapModel = new GoogleMap({
      mapProperties: {
        ...GoogleMapsService.getApplicationMapProperties(),
        ...mapProperties
      },
      uiProperties: {
        ...GoogleMapsService.getApplicationUiProperties(),
        ...uiProperties
      }
    });

    // create GoogleMap element
    const map = new window.google.maps.Map(
      GoogleMapsService.bindMapToElement(elementId),
      {
        center: mapModel.getCenter(),
        controlSize: mapModel.getUiSize(),
        disableDefaultUI: mapModel.getUiDisabled(),
        fullscreenControl: !mapModel.getUiDisableFullscreenControl(),
        mapTypeControl: !mapModel.getUiDisableMapTypeControl(),
        mapTypeControlOptions: {
          position: window.google.maps.ControlPosition[mapModel.getUiPosition()],
          style: window.google.maps.MapTypeControlStyle.MAP_DEFAULT
        },
        options: {
          gestureHandling: mapModel.getUiGestureHandling()
        },
        scaleControl: !mapModel.getUiDisableScaleControl(),
        streetViewControl: !mapModel.getUiDisableStreetViewControl(),
        zoom: mapModel.getZoom(),
        plugins,
      }
    );

    // StreetView Params for GoogleMap if not disabled
    if(!mapModel.getUiDisableStreetViewControl()) {
      // create GoogleStreetView model
      const streetViewModel = new GoogleStreetView({
        streetViewProperties: {
          ...GoogleMapsService.getApplicationStreetViewProperties(),
          ...streetViewProperties
        },
        uiProperties: {
          ...GoogleMapsService.getApplicationUiProperties(),
          ...uiProperties
        }
      });

      const streetViewParams = {
        addressControlOptions: { position: window.google.maps.ControlPosition[streetViewModel.getUiPosition()] },
        controlSize: streetViewModel.getUiSize(),
        disableDefaultUI: streetViewModel.getUiDisabled(),
        enableCloseButton: !streetViewModel.getUiDisableCloseButton(),
        fullscreenControl: !streetViewModel.getUiDisableFullscreenControl(),
        mapTypeControl: !streetViewModel.getUiDisableMapTypeControl(),
        mapTypeControlOptions: {
          position: window.google.maps.ControlPosition[streetViewModel.getUiPosition()],
          style: window.google.maps.MapTypeControlStyle.MAP_DEFAULT
        },
        panControl: !streetViewModel.getUiDisablePanControl(),
        position: streetViewModel.getPosition(),
        pov: streetViewModel.getPov(),
        options: {
          gestureHandling: streetViewModel.getUiGestureHandling()
        },
        scaleControl: !streetViewModel.getUiDisableScaleControl(),
        streetViewControl: !streetViewModel.getUiDisableStreetViewControl(),
        plugins,
      };
      map.getStreetView().setOptions(streetViewParams);
    }
    return map;
  }

  static async createStreetView(elementId, streetViewProperties, uiProperties, plugins) {
    await GoogleMapsService.initialize();

    // create GoogleStreetView model
    const streetViewModel = new GoogleStreetView({
      streetViewProperties: {
        ...GoogleMapsService.getApplicationStreetViewProperties(),
        ...streetViewProperties
      },
      uiProperties: {
        ...GoogleMapsService.getApplicationUiProperties(),
        ...uiProperties
      }
    });

    // create GoogleStreetView element
    return new window.google.maps.StreetViewPanorama(
      GoogleMapsService.bindMapToElement(elementId),
      {
        addressControlOptions: { position: window.google.maps.ControlPosition[streetViewModel.getUiPosition()] },
        controlSize: streetViewModel.getUiSize(),
        disableDefaultUI: streetViewModel.getUiDisabled(),
        enableCloseButton: !streetViewModel.getUiDisableCloseButton(),
        fullscreenControl: !streetViewModel.getUiDisableFullscreenControl(),
        mapTypeControl: !streetViewModel.getUiDisableMapTypeControl(),
        mapTypeControlOptions: {
          position: window.google.maps.ControlPosition[streetViewModel.getUiPosition()],
          style: window.google.maps.MapTypeControlStyle.MAP_DEFAULT
        },
        panControl: !streetViewModel.getUiDisablePanControl(),
        position: streetViewModel.getPosition(),
        pov: streetViewModel.getPov(),
        options: {
          gestureHandling: streetViewModel.getUiGestureHandling()
        },
        scaleControl: !streetViewModel.getUiDisableScaleControl(),
        source: window.google.maps.StreetViewSource.OUTDOOR,
        streetViewControl: !streetViewModel.getUiDisableStreetViewControl(),
        plugins,
      }
    );
  }

  /*====================
  Helper Functions
  ====================*/
  static bindMapToElement(elementId) {
    const existingMapElement = window.document.getElementById(elementId);
    if(existingMapElement) return existingMapElement;
    else {
      const newMapElement = window.document.createElement("div");
      newMapElement.id = elementId;
      return newMapElement;
    }
  }

  static navigateToPosition(position) {
    window.open(`${GOOGLE_MAPS_ROUTE_PREFIX}${position.lat},${position.lng}${GOOGLE_MAPS_ROUTE_SUFFIX}`);
  }
}
export default GoogleMapsService;
